import React, { Fragment, useMemo, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { ProductCartRedIcon } from "@components/Icons";

import { useFlags } from "flagsmith/react";
import { USER_FLAGS_ENUM } from "@utils/featureFlags";

import type {
  ICartView,
  PaymentMethod,
} from "../../../model/product.cart.model";

import {
  CatalogOption,
  formatQty,
  parseDotQuantity,
  useProductsStep,
} from "../../hook/useProductsStep";

import { formatBRL } from "@utils/currency";
import { cardOutlinedSx } from "../purchase/styles";

export function ProductsStepDesktop() {
  const theme = useTheme();
  const { state, action } = useProductsStep();

  const flags = useFlags([USER_FLAGS_ENUM.CASH_PAYMENT]);
  const isCashPaymentEnabled =
    flags[USER_FLAGS_ENUM.CASH_PAYMENT]?.enabled === true;

  const isLoading = state.pricingStatus === "loading";
  const disabled = isLoading || !state.canInteract;

  const hasItems = state.cartView.length > 0;

  const allCash = useMemo(
    () => state.cartView.every((c) => c.payment === "T"),
    [state.cartView]
  );

  const [selected, setSelected] = useState<CatalogOption | null>(null);

  async function onSelect(opt: CatalogOption | null) {
    setSelected(opt);
    if (!opt) return;

    setSelected(null);

    void action.addProduct({
      productId: opt.key,
      code: opt.code ?? opt.key,
      description: opt.label,
      quantity: 0,
    });
  }

  function renderLoadingHint() {
    if (!isLoading) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <LinearProgress />
        <Typography variant="caption" sx={{ opacity: 0.75 }}>
          Calculando preço e atualizando carrinho...
        </Typography>
      </Box>
    );
  }

  function renderEmptyState() {
    return (
      <TableRow>
        <TableCell
          data-col="estado-vazio"
          colSpan={9}
          sx={{ textAlign: "center", borderBottom: "none" }}
        >
          <ProductCartRedIcon style={{ fontSize: 100, margin: 30 }} />
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Nenhum produto adicionado.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  function renderHeaderRow() {
    if (!hasItems) return null;

    return (
      <Fragment>
        <TableCell data-col="codigo-produto" align="center">
          Código
        </TableCell>
        <TableCell data-col="descricao-produto" align="left">
          Produto
        </TableCell>
        <TableCell data-col="valor-litro" align="right">
          Valor do Litro
        </TableCell>
        <TableCell data-col="subtotal" align="right">
          Subtotal
        </TableCell>
        <TableCell data-col="forma-pagamento" align="center">
          Forma de pagamento
        </TableCell>
        <TableCell data-col="quantidade" align="center">
          Quantidade
        </TableCell>
        {!allCash && (
          <TableCell data-col="prazo-pagamento" align="center">
            Prazo
          </TableCell>
        )}
        <TableCell data-col="acoes" align="right" />
      </Fragment>
    );
  }

  function renderBodyRow(it: ICartView) {
    const qtyInvalid = !Number.isFinite(it.quantity) || it.quantity === 0;

    return (
      <TableRow key={it.id} data-row="item-carrinho">
        <TableCell
          data-col="codigo-produto"
          align="center"
          sx={{ opacity: 0.9 }}
        >
          {it.code}
        </TableCell>

        <TableCell
          data-col="descricao-produto"
          align="left"
          sx={{ minWidth: 200 }}
        >
          <Typography fontWeight={500} sx={{ opacity: 0.9 }}>
            {it.description}
          </Typography>
        </TableCell>

        <TableCell
          data-col="valor-litro"
          align="right"
          sx={{ fontWeight: 700, opacity: 0.9 }}
        >
          {formatBRL(it.displayUnitPrice, { fractionDigits: 4 })}
        </TableCell>

        <TableCell
          data-col="subtotal"
          align="right"
          sx={{ fontWeight: 700, color: "success.main" }}
        >
          {formatBRL(it.subtotal, { fractionDigits: 2 })}
        </TableCell>

        <TableCell data-col="forma-pagamento" align="center">
          <RadioGroup
            row
            value={it.payment}
            onChange={(e) => {
              const selectedValue = e.target.value as PaymentMethod;
              if (selectedValue === "T" && !isCashPaymentEnabled) return;
              void action.changePayment(it.id, selectedValue);
            }}
            sx={{ justifyContent: "center" }}
          >
            <FormControlLabel
              value="T"
              control={
                <Radio
                  size="small"
                  sx={{
                    "&.Mui-checked": {
                      color: "error.main",
                    },
                    "&.Mui-disabled.Mui-checked": {
                      color: "error.main",
                    },
                  }}
                />
              }
              label="A vista"
              sx={{ mr: 2 }}
              disabled={!isCashPaymentEnabled}
            />
            <FormControlLabel
              value="B"
              control={
                <Radio
                  size="small"
                  sx={{
                    "&.Mui-checked": {
                      color: "error.main",
                    },
                    "&.Mui-disabled.Mui-checked": {
                      color: "error.main",
                    },
                  }}
                />
              }
              label="Boleto"
              disabled={false}
            />
          </RadioGroup>
        </TableCell>

        <TableCell data-col="quantidade" align="center">
          <TextField
            select
            value={String(it.quantity)}
            onChange={(e) => void action.changeQty(it.id, e.target.value)}
            disabled={state.pricingStatus === "loading"}
            error={qtyInvalid}
            size="small"
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) =>
                selected === "0" ? (
                  <span
                    style={{
                      color: theme.palette.text.secondary,
                      fontSize: "1.2rem",
                    }}
                  >
                    Selecione
                  </span>
                ) : (
                  formatQty(selected as string)
                ),
            }}
            sx={{
              width: 110,
              "& .MuiOutlinedInput-root": { borderRadius: "1rem" },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid rgba(0,0,0,0.23)",
              },
            }}
          >
            <MenuItem value="0" disabled>
              Selecione
            </MenuItem>
            {state.quantityOptions.map((opt) => (
              <MenuItem key={opt.value} value={parseDotQuantity(opt.label)}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </TableCell>

        {!allCash && (
          <TableCell data-col="prazo-pagamento" align="center">
            {it.payment !== "T" && (
              <TextField
                select
                value={String(it.deadlinePayment)}
                onChange={(e) =>
                  void action.changeDeadline(it.id, e.target.value)
                }
                disabled={state.pricingStatus === "loading"}
                size="small"
                sx={{
                  width: 110,
                  "& .MuiOutlinedInput-root": { borderRadius: "1rem" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid rgba(0,0,0,0.23)",
                  },
                }}
              >
                {it.paymentDayOptions
                  .filter((o) =>
                    it.payment === "B" ? Number(o.key) > 0 : true
                  )
                  .map((o) => {
                    const days = Number(o.key);
                    return (
                      <MenuItem key={o.key} value={String(days)}>
                        {days === 0
                          ? "A vista"
                          : `${days} ${days === 1 ? "Dia" : "Dias"}`}
                      </MenuItem>
                    );
                  })}
              </TextField>
            )}
          </TableCell>
        )}

        <TableCell data-col="acoes" align="right">
          <IconButton onClick={() => action.remove(it.id)} aria-label="remover">
            <DeleteOutlineIcon color="error" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Card variant="outlined" sx={cardOutlinedSx}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.25}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" fontWeight={600}>
              Produtos
            </Typography>

            {renderLoadingHint()}

            <IconButton aria-label="carrinho" disabled>
              <ShoppingCartOutlinedIcon />
            </IconButton>
          </Box>

          <TextField
            select
            label="Descrição do produto"
            value={selected?.key ?? ""}
            onChange={(e) => {
              const next =
                state.catalogOptions.find((o) => o.key === e.target.value) ??
                null;
              void onSelect(next);
            }}
            disabled={disabled}
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "1rem" } }}
          >
            {state.catalogOptions.length === 0 ? (
              <MenuItem value="" disabled>
                Nenhum produto disponível
              </MenuItem>
            ) : (
              state.catalogOptions.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  {opt.label}
                </MenuItem>
              ))
            )}
          </TextField>

          {!state.canInteract && (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Selecione cliente, endereço e base antes de adicionar produtos.
            </Typography>
          )}

          {state.error && (
            <Typography variant="body2" color="error">
              {state.error}
            </Typography>
          )}

          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 980,
                "& th": { fontWeight: 700, whiteSpace: "nowrap" },
                "& td": { whiteSpace: "nowrap" },
              }}
            >
              <TableHead>
                <TableRow>{renderHeaderRow()}</TableRow>
              </TableHead>

              <TableBody>
                {!hasItems
                  ? renderEmptyState()
                  : state.cartView.map((item) => renderBodyRow(item))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ px: { lg: 15 } }}>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{ opacity: 0.85 }}
            >
              <strong>Atenção:</strong> Para garantir melhor atendimento,
              recomendamos que os pedidos sejam programados com pelo menos 24h
              de antecedência. Os valores informados refletem os preços do dia e
              estão sujeitos a alterações até a data do faturamento.
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
